/**
 * Cart Version Guard
 * -------------------
 * ALBAU-855 - Legacy Basket Optimisation
 *
 * Personalised cart lines added under an older cart version are flagged by Liquid in the cart drawer
 * (snippets/cart-line-version-status.liquid): the row gets `data-cart-outdated` and a "remove it and add it
 * again" message, a bar sits above the footer and the checkout button is rendered disabled.
 * Nothing is removed automatically.
 *
 * This module:
 * - blocks checkout while any outdated line is in the drawer, in case the button state is out of date,
 *   and scrolls to the first outdated line;
 * - keeps the checkout button in sync when cart.js refreshes the drawer without re-rendering its footer;
 * - publishes Shopify analytics custom events (Settings > Customer events, for pixels such as GA4/Meta):
 *   `legacy_basket_triggered` when the cart drawer opens with outdated lines (once per set of lines per
 *   session) and `legacy_basket_resolved` once those lines have left the cart.
 */

declare global {
  interface Window {
    Shopify?: { analytics?: { publish?: (name: string, data: Record<string, unknown>) => void } };
  }
}

export const CHECKOUT_BLOCKED_HINT_ID = 'CartDrawer-CheckoutBlocked';
const OUTDATED_LINE_SELECTOR = '[data-cart-outdated]';
const CHECKOUT_SELECTOR = '[name="checkout"]';
const SYNCED_CHECKOUT_ATTRIBUTES = ['disabled', 'aria-disabled', 'aria-describedby'];
const LEGACY_BASKET_STORAGE_KEY = 'al:legacy-basket';

export const CART_VERSION_EVENTS = {
  triggered: 'legacy_basket_triggered',
  resolved: 'legacy_basket_resolved'
} as const;

export interface OutdatedLine {
  line_key: string;
  product_id: string;
  variant_id: string;
  product_title: string;
  quantity: number;
  line_version: number;
  product_version: number;
}

/** Reads an outdated row's details (rendered by cart-item-row.liquid). */
export const readOutdatedLine = (row: Element): OutdatedLine => {
  const data = (row as HTMLElement).dataset;
  return {
    line_key: data.cartVersionLineKey || '',
    product_id: data.cartVersionProductId || '',
    variant_id: data.cartVersionVariantId || '',
    product_title: data.cartVersionProductTitle || '',
    quantity: Number(data.cartVersionQuantity) || 1,
    line_version: Number(data.cartVersionLineVersion) || 1,
    product_version: Number(data.cartVersionProductVersion) || 1
  };
};

export const readOutdatedLines = (root: ParentNode = document): OutdatedLine[] =>
  Array.from(root.querySelectorAll(OUTDATED_LINE_SELECTOR)).map(readOutdatedLine);

/** Publishes a Shopify custom event; does nothing where Shopify analytics isn't loaded (e.g. theme editor). */
export const publishCartVersionEvent = (name: string, lines: OutdatedLine[]): void => {
  if (lines.length === 0) return;
  try {
    window.Shopify?.analytics?.publish?.(name, { lines, count: lines.length });
  } catch {
    // Analytics must never break the cart.
  }
};

/** The outdated lines reported as `legacy_basket_triggered` this session and not yet resolved. */
const readTriggeredLines = (): OutdatedLine[] => {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(LEGACY_BASKET_STORAGE_KEY) || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

const writeTriggeredLines = (lines: OutdatedLine[]): void => {
  try {
    if (lines.length > 0) window.sessionStorage.setItem(LEGACY_BASKET_STORAGE_KEY, JSON.stringify(lines));
    else window.sessionStorage.removeItem(LEGACY_BASKET_STORAGE_KEY);
  } catch {
    // Storage blocked: events may repeat on the next page.
  }
};

/**
 * Publishes the legacy basket events from the current cart drawer:
 * - `legacy_basket_triggered` when the drawer is open with outdated lines not reported yet this session;
 * - `legacy_basket_resolved` when lines reported as triggered are no longer outdated in the cart (removed).
 */
export const publishLegacyBasketEvents = (): void => {
  if (!document.querySelector('cart-drawer')) return;
  const outdatedLines = readOutdatedLines();
  const triggeredLines = readTriggeredLines();
  const outdatedKeys = new Set(outdatedLines.map((line) => line.line_key));

  const resolvedLines = triggeredLines.filter((line) => !outdatedKeys.has(line.line_key));
  if (resolvedLines.length > 0 && outdatedLines.length === 0) {
    publishCartVersionEvent(CART_VERSION_EVENTS.resolved, resolvedLines);
    writeTriggeredLines([]);
    return;
  }

  if (!document.querySelector('cart-drawer.active')) return;
  const triggeredKeys = new Set(triggeredLines.map((line) => line.line_key));
  const newLines = outdatedLines.filter((line) => !triggeredKeys.has(line.line_key));
  if (newLines.length === 0) return;

  publishCartVersionEvent(CART_VERSION_EVENTS.triggered, outdatedLines);
  writeTriggeredLines(outdatedLines);
};

export const hasOutdatedLines = (root: ParentNode = document): boolean =>
  root.querySelector(OUTDATED_LINE_SELECTOR) !== null;

/**
 * Stops a checkout attempt while outdated lines are in the cart and points the customer at them.
 * @returns Whether checkout was blocked.
 */
export const blockCheckoutIfOutdated = (event: Event): boolean => {
  if (!hasOutdatedLines()) return false;

  event.preventDefault();
  event.stopPropagation();
  document.querySelector(OUTDATED_LINE_SELECTOR)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return true;
};

/**
 * Copies the blocked state of the freshly rendered checkout button (and its bar) onto the live one.
 * @param target - The checkout button in the page.
 * @param source - The checkout button in the freshly rendered drawer.
 * @param sourceRoot - The freshly rendered drawer document.
 */
export const syncCheckoutBlockedState = (target: Element, source: Element, sourceRoot: ParentNode): void => {
  SYNCED_CHECKOUT_ATTRIBUTES.forEach((attribute) => {
    const value = source.getAttribute(attribute);
    if (value === null) target.removeAttribute(attribute);
    else target.setAttribute(attribute, value);
  });

  const targetHint = document.getElementById(CHECKOUT_BLOCKED_HINT_ID);
  const sourceHint = sourceRoot.querySelector(`#${CHECKOUT_BLOCKED_HINT_ID}`);
  if (targetHint && sourceHint) targetHint.toggleAttribute('hidden', sourceHint.hasAttribute('hidden'));
};

const onClick = (event: MouseEvent): void => {
  const target = event.target instanceof Element ? event.target : null;
  if (target?.closest(CHECKOUT_SELECTOR)) blockCheckoutIfOutdated(event);
};

const onSubmit = (event: SubmitEvent): void => {
  if (event.submitter?.getAttribute('name') === 'checkout') blockCheckoutIfOutdated(event);
};

let initialised = false;

export const initCartVersionGuard = (): void => {
  if (initialised) return;
  initialised = true;

  // Capture phase so the check runs before any other checkout handler.
  document.addEventListener('click', onClick, true);
  document.addEventListener('submit', onSubmit, true);

  // The drawer opens (class `active`) and re-renders its rows in several ways; check after each change.
  let scheduled: number | undefined;
  const scheduleShownCheck = () => {
    window.clearTimeout(scheduled);
    scheduled = window.setTimeout(publishLegacyBasketEvents, 300);
  };
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    new MutationObserver(scheduleShownCheck).observe(drawer, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }
  scheduleShownCheck();
};
